<template>
  <app-page labelledby="page-title">
    <page-header title="Новая организация" description="Найдите организацию по ИНН, проверьте реквизиты и добавьте её в рабочее пространство." />

    <nav class="create-steps" aria-label="Этапы создания организации">
      <span v-for="item in steps" :key="item.id" :class="{ active: step === item.id, complete: step > item.id }">
        <q-icon :name="step > item.id ? 'check' : item.icon" /> {{ item.label }}
      </span>
    </nav>

    <page-section v-if="step === 1" title="Поиск организации" description="Поиск выполняется по открытым данным ФНС через DaData.">
      <form class="lookup-form" @submit.prevent="lookupOrganisation">
        <q-input v-model="inn" mask="## ## ###### ##" label="ИНН" autocomplete="off" :rules="[validateInn]" />
        <q-btn color="primary" no-caps type="submit" label="Найти" :loading="lookupLoading" />
      </form>
      <app-state
        v-if="lookupState"
        :state="lookupState"
        :title="lookupStateTitle"
        :message="lookupStateMessage"
        :action-label="lookupState === 'error' ? 'Повторить' : ''"
        @retry="lookupOrganisation"
      />
    </page-section>

    <template v-else-if="step === 2">
      <page-section title="Подтверждение данных" description="Проверьте сведения: после создания они будут использоваться в документах.">
        <dl class="requisites">
          <div v-for="field in lookupFields" :key="field.label" :class="{ 'requisites__wide': field.wide }">
            <dt>{{ field.label }}</dt><dd>{{ field.value || 'Не указано' }}</dd>
          </div>
        </dl>
        <q-toggle v-model="isDirector" class="q-mt-lg" label="Я являюсь руководителем организации" />
      </page-section>
      <div class="create-actions">
        <q-btn flat no-caps label="Назад" @click="resetLookup" />
        <q-btn color="primary" no-caps label="Создать организацию" :loading="createLoading" @click="createOrganization" />
      </div>
    </template>

    <page-section v-else title="Организация создана" description="Контекст организации и права доступа синхронизированы.">
      <div class="success-summary">
        <q-icon name="check_circle" color="positive" size="2.5rem" />
        <div><strong>{{ organization.name }}</strong><span>ИНН {{ organization.inn }}</span></div>
      </div>
      <div class="create-actions">
        <q-btn color="primary" no-caps label="Перейти к организации" :loading="!orgContextReady" :disable="!orgContextReady" @click="routeToOrg" />
      </div>
    </page-section>
  </app-page>
</template>

<script>
import { formatDate } from '../../services/dateFormat'
import AppPage from '../../components/ui/AppPage.vue'
import AppState from '../../components/ui/AppState.vue'
import PageHeader from '../../components/ui/PageHeader.vue'
import PageSection from '../../components/ui/PageSection.vue'

export default {
  name: 'CreateOrganization',
  components: { AppPage, AppState, PageHeader, PageSection },
  data() {
    return {
      step: 1, inn: '', lookupData: null, organization: null, isDirector: true,
      lookupLoading: false, createLoading: false, lookupState: '', lookupStateTitle: '', lookupStateMessage: '',
      lookupRequestId: '', createRequestId: '', orgContextRequestId: '', createdOrgId: null, orgContextReady: false,
      steps: [
        { id: 1, label: 'Поиск', icon: 'search' },
        { id: 2, label: 'Проверка', icon: 'fact_check' },
        { id: 3, label: 'Готово', icon: 'check_circle' }
      ]
    }
  },
  computed: {
    lookupFields() {
      const data = this.lookupData || {}
      return [
        { label: 'Полное наименование', value: data.name, wide: true },
        { label: 'ИНН', value: data.inn }, { label: 'ОГРН', value: data.ogrn },
        { label: 'Организационно-правовая форма', value: data.opf }, { label: 'Статус', value: data.statusLabel },
        { label: 'Юридический адрес', value: data.address, wide: true },
        { label: 'Руководитель', value: data.director || data.fio, wide: true },
        { label: 'Дата регистрации', value: data.registrationDate }, { label: 'Основной ОКВЭД', value: data.okved }
      ]
    }
  },
  created() {
    this.onLookup = (response) => {
      if (!this.matches(response, this.lookupRequestId)) return
      this.lookupLoading = false
      if (response.error || !response.info) {
        this.lookupState = response.error === 'not_found' || !response.error ? 'empty' : 'error'
        this.lookupStateTitle = this.lookupState === 'empty' ? 'Организация не найдена' : 'Не удалось выполнить поиск'
        this.lookupStateMessage = this.lookupState === 'empty' ? 'Проверьте ИНН или введите другой.' : 'Сервис поиска временно недоступен. Повторите запрос.'
        return
      }
      const info = response.info; const legal = info.type === 'LEGAL'
      this.lookupData = {
        name: info.name?.full_with_opf || info.name?.short_with_opf || '', inn: info.inn || this.inn.replace(/\s/g, ''),
        ogrn: info.ogrn || '', address: info.address?.unrestricted_value || info.address?.value || '',
        director: legal && info.management ? `${info.management.post || ''} ${info.management.name || ''}`.trim() : '',
        fio: !legal && info.fio ? `${info.fio.surname || ''} ${info.fio.name || ''} ${info.fio.patronymic || ''}`.trim() : '',
        opf: info.opf?.full || '', status: info.state?.status || '', statusLabel: info.state?.status === 'ACTIVE' ? 'Действующая' : (info.state?.status || 'Не указан'),
        registrationDate: info.ogrn_date ? formatDate(new Date(info.ogrn_date)) : '', okved: info.okved || '', is_ooo: legal
      }
      this.step = 2
    }
    this.onCreated = (response) => {
      if (!this.matches(response, this.createRequestId)) return
      this.createLoading = false
      if (response.error || !response.organisation_id) { this.$q.notify({ color: 'negative', message: 'Не удалось создать организацию' }); return }
      this.createdOrgId = response.organisation_id; this.connection.updateOrgId(this.$q, response.organisation_id, this.$store)
      this.organization = { name: response.organisation_metadata?.display_name || response.organisation_metadata?.name || this.lookupData.name, inn: this.lookupData.inn }
      this.step = 3; this.orgContextReady = false
      this.connection.getOrganisationInfo(this.connection.makeRequestId('created-org-info'), this.createdOrgId)
      this.orgContextRequestId = this.connection.makeRequestId('created-org-user')
      this.connection.getUserInfoAcrossOrg(this.orgContextRequestId, this.createdOrgId)
    }
    this.onOrgContext = (response) => {
      if (!this.matches(response, this.orgContextRequestId) || String(response.current_org || response.organisation_id) !== String(this.createdOrgId)) return
      if (response.error || !response.user?.role) { this.$q.notify({ color: 'negative', message: 'Организация создана, но права ещё не синхронизированы' }); return }
      this.orgContextReady = true
    }
    this.emitter.on('didReceiveAgentInfoByInn', this.onLookup)
    this.emitter.on('organisationCreated', this.onCreated)
    this.emitter.on('didReceoveUserInfoAcrossOrg', this.onOrgContext)
  },
  beforeUnmount() {
    this.emitter.off('didReceiveAgentInfoByInn', this.onLookup)
    this.emitter.off('organisationCreated', this.onCreated)
    this.emitter.off('didReceoveUserInfoAcrossOrg', this.onOrgContext)
  },
  methods: {
    validateInn(value) { const length = (value || '').replace(/\s/g, '').length; return [10, 12].includes(length) || 'Введите ИНН из 10 или 12 цифр' },
    matches(response, id) { return !!id && [response.request_id, response.requesrt_id, response.id].includes(id) },
    lookupOrganisation() {
      if (this.validateInn(this.inn) !== true) return
      this.lookupLoading = true; this.lookupState = ''; this.lookupData = null
      this.lookupRequestId = this.connection.makeRequestId('org-lookup'); this.connection.lookupOrganisationByInn(this.lookupRequestId, this.inn.replace(/\s/g, ''))
    },
    resetLookup() { this.step = 1; this.lookupData = null; this.lookupState = '' },
    createOrganization() {
      this.createLoading = true; this.createRequestId = this.connection.makeRequestId('org-create')
      this.connection.createOrganisation(this.lookupData.inn, this.isDirector, this.createRequestId)
    },
    routeToOrg() { if (this.orgContextReady) this.$router.push({ name: 'org_main' }) }
  }
}
</script>

<style scoped>
.create-steps { display: flex; gap: var(--sid-space-2); margin-bottom: var(--sid-space-6); color: var(--sid-color-text-secondary); }
.create-steps span { display: inline-flex; align-items: center; gap: var(--sid-space-1); padding: var(--sid-space-2) var(--sid-space-3); border-radius: var(--sid-radius-pill); background: var(--sid-color-surface-subtle); }
.create-steps .active { background: var(--sid-color-info-surface); color: var(--sid-color-info); font-weight: var(--sid-font-weight-semibold); }
.create-steps .complete { color: var(--sid-color-success); }
.lookup-form { display: grid; max-width: 34rem; grid-template-columns: minmax(0, 1fr) auto; align-items: start; gap: var(--sid-space-3); }
.requisites { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-4) var(--sid-space-6); margin: 0; }
.requisites__wide { grid-column: 1 / -1; }
.requisites dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.requisites dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
.create-actions { display: flex; justify-content: flex-end; gap: var(--sid-space-2); margin-top: var(--sid-space-6); }
.success-summary { display: flex; align-items: center; gap: var(--sid-space-3); }
.success-summary div { display: flex; min-width: 0; flex-direction: column; }
.success-summary strong { overflow-wrap: anywhere; }
.success-summary span { color: var(--sid-color-text-secondary); }
@media (max-width: 599px) {
  .create-steps span { flex: 1; justify-content: center; padding-inline: var(--sid-space-2); }
  .lookup-form, .requisites { grid-template-columns: 1fr; }
  .requisites__wide { grid-column: auto; }
  .create-actions { align-items: stretch; flex-direction: column-reverse; }
}
</style>
